import { registerFeatureBootstrap } from "../bootstrap";
import { submitPulse } from "./pulse/api";
import "./library_hub";
import "./skills_hub";
import "./social_capital_hub";

type PulsePromptDetail = {
  touchpoint?: string;
  ref_id?: string;
  title?: string;
  subtitle?: string;
  message?: string;
};

function pulseSheet(doc: Document = document): HTMLElement | null {
  return doc.getElementById("participant-pulse-sheet");
}

function pulseForm(sheet: ParentNode = document): HTMLFormElement | null {
  if (!(sheet instanceof HTMLElement)) return null;
  const form = sheet.querySelector("[data-pulse-form]");
  return form instanceof HTMLFormElement ? form : null;
}

function pulseToast(message: string, variant: "success" | "error" | "warning" | "info" = "info"): HTMLElement | null {
  const api = window.WMUToast;
  if (!api) return null;
  if (typeof api.show === "function") {
    return api.show(message, { variant, duration: 4200 });
  }
  if (typeof api === "function") {
    return api(message, { variant, duration: 4200 }) ?? null;
  }
  return null;
}

async function postPulse(payload: Record<string, string>): Promise<void> {
  const result = await submitPulse({
    touchpoint: payload.touchpoint || "",
    ref_id: payload.ref_id || "",
    reaction: payload.reaction || "",
    comment: payload.comment || "",
  });
  if (!result.ok) {
    const message = typeof result.error.body === "string" ? result.error.body.trim() : "";
    throw new Error(message || "Could not save pulse.");
  }
}

function setSelectedReaction(form: HTMLFormElement, reaction: string): void {
  const hidden = form.querySelector("[data-pulse-reaction]");
  if (hidden instanceof HTMLInputElement) {
    hidden.value = reaction;
  }
  form.querySelectorAll("[data-pulse-reaction-option]").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.classList.toggle("is-active", node.getAttribute("data-reaction") === reaction);
  });
}

function resetPulseForm(form: HTMLFormElement): void {
  setSelectedReaction(form, "");
  const ref = form.querySelector("[data-pulse-ref-id]");
  const touchpoint = form.querySelector("[data-pulse-touchpoint]");
  const comment = form.querySelector("textarea[name='comment']");
  const subtitle = document.querySelector("[data-pulse-sheet-subtitle]");
  const title = document.getElementById("participant-pulse-sheet-title");
  if (ref instanceof HTMLInputElement) ref.value = "";
  if (touchpoint instanceof HTMLInputElement) touchpoint.value = "open_pulse";
  if (comment instanceof HTMLTextAreaElement) {
    comment.value = "";
    comment.dispatchEvent(new Event("input", { bubbles: true }));
  }
  if (subtitle instanceof HTMLElement) {
    subtitle.textContent = "Share a quick pulse. It stays anonymous in GM views.";
  }
  if (title instanceof HTMLElement) {
    title.textContent = "How's the event going for you right now?";
  }
}

function openPulseSheet(detail?: PulsePromptDetail): void {
  const sheet = pulseSheet(document);
  const form = pulseForm(sheet || document);
  if (!(form instanceof HTMLFormElement) || !(sheet instanceof HTMLElement)) return;

  resetPulseForm(form);
  const title = document.getElementById("participant-pulse-sheet-title");
  const subtitle = document.querySelector("[data-pulse-sheet-subtitle]");
  const ref = form.querySelector("[data-pulse-ref-id]");
  const touchpoint = form.querySelector("[data-pulse-touchpoint]");

  if (title instanceof HTMLElement && typeof detail?.title === "string" && detail.title.trim()) {
    title.textContent = detail.title.trim();
  }
  if (subtitle instanceof HTMLElement && typeof detail?.subtitle === "string" && detail.subtitle.trim()) {
    subtitle.textContent = detail.subtitle.trim();
  }
  if (ref instanceof HTMLInputElement) {
    ref.value = typeof detail?.ref_id === "string" ? detail.ref_id.trim() : "";
  }
  if (touchpoint instanceof HTMLInputElement) {
    touchpoint.value = typeof detail?.touchpoint === "string" && detail.touchpoint.trim() ? detail.touchpoint.trim() : "open_pulse";
  }
  if (window.WMSheet && typeof window.WMSheet.open === "function") {
    window.WMSheet.open(sheet);
    return;
  }
  sheet.setAttribute("aria-hidden", "false");
}

function bindOpenTrigger(node: HTMLElement): void {
  node.addEventListener("click", () => openPulseSheet());
}

function bindScanRow(node: HTMLElement): void {
  const refID = String(node.getAttribute("data-ref-id") || "").trim();
  const dismiss = () => {
    node.classList.add("is-dismissed");
    window.setTimeout(() => node.remove(), 180);
  };
  node.querySelectorAll("[data-pulse-reaction-option]").forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.addEventListener("click", async () => {
      const reaction = String(button.getAttribute("data-reaction") || "").trim();
      if (!reaction || !refID || node.dataset.state === "busy") return;
      node.dataset.state = "busy";
      dismiss();
      try {
        await postPulse({ touchpoint: "scan", ref_id: refID, reaction });
      } catch (error) {
        pulseToast(error instanceof Error ? error.message : "Could not save pulse.", "error");
      }
    });
  });
  const dismissButton = node.querySelector("[data-pulse-dismiss]");
  if (dismissButton instanceof HTMLButtonElement) {
    dismissButton.addEventListener("click", dismiss);
  }
}

function bindPulseForm(sheet: HTMLElement): void {
  const form = pulseForm(sheet);
  if (!(form instanceof HTMLFormElement)) return;

  form.querySelectorAll("[data-pulse-reaction-option]").forEach((node) => {
    if (!(node instanceof HTMLButtonElement)) return;
    node.addEventListener("click", () => {
      setSelectedReaction(form, String(node.getAttribute("data-reaction") || ""));
    });
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (form.dataset.state === "busy") return;
    const reactionNode = form.querySelector("[data-pulse-reaction]");
    const touchpointNode = form.querySelector("[data-pulse-touchpoint]");
    const refNode = form.querySelector("[data-pulse-ref-id]");
    const commentNode = form.querySelector("textarea[name='comment']");

    const reaction = reactionNode instanceof HTMLInputElement ? reactionNode.value.trim() : "";
    if (!reaction) {
      pulseToast("Pick one reaction first.", "warning");
      return;
    }
    form.dataset.state = "busy";
    const submit = form.querySelector("button[type='submit']");
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    try {
      await postPulse({
        touchpoint: touchpointNode instanceof HTMLInputElement ? touchpointNode.value.trim() : "open_pulse",
        ref_id: refNode instanceof HTMLInputElement ? refNode.value.trim() : "",
        reaction,
        comment: commentNode instanceof HTMLTextAreaElement ? commentNode.value.trim() : "",
      });
      pulseToast("Thanks for the pulse.", "success");
      if (window.WMSheet && typeof window.WMSheet.close === "function") {
        window.WMSheet.close("participant-pulse-sheet");
      }
      resetPulseForm(form);
    } catch (error) {
      pulseToast(error instanceof Error ? error.message : "Could not save pulse.", "error");
    } finally {
      form.dataset.state = "";
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
    }
  });

  form.querySelectorAll("[data-pulse-skip]").forEach((node) => {
    if (!(node instanceof HTMLButtonElement)) return;
    node.addEventListener("click", () => {
      resetPulseForm(form);
      if (window.WMSheet && typeof window.WMSheet.close === "function") {
        window.WMSheet.close("participant-pulse-sheet");
      }
    });
  });
}

function handlePulsePrompt(detail: PulsePromptDetail): void {
  const message = String(detail?.message || "").trim();
  const toast = pulseToast(message || "A schedule item just ended. Tap to rate.", "info");
  if (toast instanceof HTMLElement) {
    toast.style.cursor = "pointer";
    toast.addEventListener("click", () => openPulseSheet(detail));
    return;
  }
  openPulseSheet(detail);
}

const pulseSheetCleanup = new WeakMap<HTMLElement, () => void>();

function mountPulseSheet(sheet: HTMLElement): void {
  bindPulseForm(sheet);
  const onPulsePrompt = (event: Event): void => {
    const custom = event as CustomEvent<PulsePromptDetail>;
    handlePulsePrompt(custom.detail || {});
  };
  document.addEventListener("wmu:pulse-prompt", onPulsePrompt);
  pulseSheetCleanup.set(sheet, () => {
    document.removeEventListener("wmu:pulse-prompt", onPulsePrompt);
  });
}

function unmountPulseSheet(sheet: HTMLElement): void {
  pulseSheetCleanup.get(sheet)?.();
  pulseSheetCleanup.delete(sheet);
}

registerFeatureBootstrap({
  key: "pulse:sheet",
  selector: "#participant-pulse-sheet",
  mount: mountPulseSheet,
  unmount: unmountPulseSheet,
});

registerFeatureBootstrap({
  key: "pulse:open-trigger",
  selector: "[data-pulse-open]",
  mount: bindOpenTrigger,
});

registerFeatureBootstrap({
  key: "pulse:scan-row",
  selector: "[data-pulse-scan-row]",
  mount: bindScanRow,
});
